import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";

import AdminHeader from "@/components/admin/AdminHeader";
import AdminBottomNav from "@/components/admin/AdminBottomNav";
import AdminMenuDrawer from "@/components/admin/AdminMenuDrawer";
import AdminCommandCenter from "@/components/admin/AdminCommandCenter";

import AdminMembersSection from "@/components/admin/AdminMembersSection";
import AdminTrainersSection from "@/components/admin/AdminTrainersSection";
import AdminProgramsSection from "@/components/admin/AdminProgramsSection";
import AdminExercisesSection from "@/components/admin/AdminExercisesSection";
import AdminNutritionSection from "@/components/admin/AdminNutritionSection";
import AdminSessionsSection from "@/components/admin/AdminSessionsSection";
import AdminSubscriptionsSection from "@/components/admin/AdminSubscriptionsSection";
import AdminSettingsSection from "@/components/admin/AdminSettingsSection";

import {
  UserCheck,
  BookOpen,
  Calendar,
  Users,
} from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active section state
  const rawView = searchParams.get("view") || "overview";
  const [activeSection, setActiveSection] = useState(rawView);
  const [sectionFilter, setSectionFilter] = useState(searchParams.get("filter") || "all");
  const [menuOpen, setMenuOpen] = useState(false);

  // Sync state with URL search param
  useEffect(() => {
    const view = searchParams.get("view") || "overview";
    setActiveSection(view);
    const filter = searchParams.get("filter") || "all";
    setSectionFilter(filter);
  }, [searchParams]);

  // Handle section navigation
  const handleNavigate = (sectionId, filter = "all") => {
    setActiveSection(sectionId);
    setSectionFilter(filter);
    const params = new URLSearchParams();
    if (sectionId !== "overview") params.set("view", sectionId);
    if (filter && filter !== "all") params.set("filter", filter);
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle Exit Admin
  const handleExitAdmin = () => {
    navigate("/", { replace: true });
  };

  // Queries for live metrics
  const { data: members = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ["admin_members"],
    queryFn: () => base44.entities.User.list(),
    enabled: user?.role === "admin",
  });

  const { data: trainers = [], isLoading: isLoadingTrainers } = useQuery({
    queryKey: ["admin_trainers"],
    queryFn: () => base44.entities.Trainer.list(),
    enabled: user?.role === "admin",
  });

  const { data: workouts = [], isLoading: isLoadingWorkouts } = useQuery({
    queryKey: ["admin_workout_sessions_count"],
    queryFn: async () => {
      try {
        return await base44.entities.WorkoutSession.list("-date", 1000);
      } catch (_) {
        return [];
      }
    },
    enabled: user?.role === "admin",
  });

  const { data: sessions = [], isLoading: isLoadingSessions } = useQuery({
    queryKey: ["admin_training_sessions"],
    queryFn: () => base44.entities.TrainingSession.list("-date", 250),
    enabled: user?.role === "admin",
  });

  const { data: exercises = [] } = useQuery({
    queryKey: ["admin_exercises_quick_check"],
    queryFn: () => base44.entities.Exercises.list("name", 1500),
    enabled: user?.role === "admin",
  });

  // Calculate stats
  const stats = useMemo(() => {
    const totalMembers = members.length;
    const activeTrainers = trainers.filter((t) => t.is_active !== false).length;
    const workoutSessions = workouts.length;
    const upcomingAppointments = sessions.filter((s) => {
      const st = (s.status || "upcoming").toLowerCase();
      if (st === "completed" || st === "cancelled") return false;
      return true;
    }).length;

    return {
      totalMembers,
      activeTrainers,
      workoutSessions,
      upcomingAppointments,
      isLoadingStats:
        isLoadingMembers || isLoadingTrainers || isLoadingWorkouts || isLoadingSessions,
    };
  }, [
    members,
    trainers,
    workouts,
    sessions,
    isLoadingMembers,
    isLoadingTrainers,
    isLoadingWorkouts,
    isLoadingSessions,
  ]);

  // Calculate Action Needed items
  const actionItems = useMemo(() => {
    // 1. Pending trainer approvals
    const pendingTrainersCount = trainers.filter((t) => t.is_active === false).length;

    // 2. Exercise media issues (missing gif/video or instructions)
    const exerciseIssuesCount = exercises.filter((ex) => {
      const hasMedia = Boolean(ex.gifUrl || ex.gif_url || ex.video_url);
      const hasInstructions = Array.isArray(ex.instructions) && ex.instructions.length > 0;
      return !hasMedia || !hasInstructions;
    }).length;

    // 3. Pending appointments
    const pendingAppointmentsCount = sessions.filter(
      (s) => (s.status || "").toLowerCase() === "pending"
    ).length;

    // 4. Member account reviews (expired memberships)
    const expiredMembersCount = members.filter((m) => {
      return m.membership_expires && new Date(m.membership_expires) < new Date();
    }).length;

    return [
      {
        id: "pending_trainers",
        title: "Pending Trainer Approvals",
        description: "Review and activate trainer profiles",
        count: pendingTrainersCount,
        icon: UserCheck,
        targetSection: "trainers",
        targetFilter: "pending",
      },
      {
        id: "exercise_media",
        title: "Exercise Media Issues",
        description: "Exercises missing GIFs or step instructions",
        count: exerciseIssuesCount,
        icon: BookOpen,
        targetSection: "exercises",
        targetFilter: "missing_media",
      },
      {
        id: "pending_appointments",
        title: "Pending Appointments",
        description: "Sessions awaiting confirmation or review",
        count: pendingAppointmentsCount,
        icon: Calendar,
        targetSection: "sessions",
        targetFilter: "pending",
      },
      {
        id: "member_reviews",
        title: "Member Account Reviews",
        description: "Members with expired memberships",
        count: expiredMembersCount,
        icon: Users,
        targetSection: "members",
        targetFilter: "expired",
      },
    ];
  }, [trainers, exercises, sessions, members]);

  // Gated access: Only authorized administrators
  if (user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-[100dvh] bg-[#030A17] text-white flex flex-col font-sans selection:bg-sky-500 selection:text-slate-950">
      {/* Dedicated Admin Header */}
      <AdminHeader
        onExitAdmin={handleExitAdmin}
        onOpenMenu={() => setMenuOpen(true)}
        user={user}
      />

      {/* Main Content Area */}
      <main className="flex-1 w-full max-w-5xl mx-auto px-3 sm:px-4 py-4">
        {activeSection === "overview" && (
          <AdminCommandCenter
            stats={stats}
            actionItems={actionItems}
            onNavigate={handleNavigate}
          />
        )}

        {activeSection === "members" && (
          <AdminMembersSection
            onBack={() => handleNavigate("overview")}
            initialFilter={sectionFilter}
          />
        )}

        {activeSection === "trainers" && (
          <AdminTrainersSection
            onBack={() => handleNavigate("overview")}
            initialFilter={sectionFilter}
          />
        )}

        {activeSection === "programs" && (
          <AdminProgramsSection
            onBack={() => handleNavigate("overview")}
            initialFilter={sectionFilter}
          />
        )}

        {activeSection === "exercises" && (
          <AdminExercisesSection
            onBack={() => handleNavigate("overview")}
            initialFilter={sectionFilter}
          />
        )}

        {activeSection === "nutrition" && (
          <AdminNutritionSection
            onBack={() => handleNavigate("overview")}
            initialFilter={sectionFilter}
          />
        )}

        {activeSection === "sessions" && (
          <AdminSessionsSection
            onBack={() => handleNavigate("overview")}
            initialFilter={sectionFilter}
          />
        )}

        {activeSection === "subscriptions" && (
          <AdminSubscriptionsSection
            onBack={() => handleNavigate("overview")}
            onNavigate={handleNavigate}
          />
        )}

        {activeSection === "settings" && (
          <AdminSettingsSection onBack={() => handleNavigate("overview")} />
        )}
      </main>

      {/* Dedicated Admin Bottom Navigation */}
      <AdminBottomNav
        activeSection={
          activeSection === "trainers" ||
          activeSection === "exercises" ||
          activeSection === "nutrition" ||
          activeSection === "subscriptions" ||
          activeSection === "settings"
            ? ""
            : activeSection
        }
        onSelectSection={(sec) => handleNavigate(sec)}
      />

      {/* Dedicated Admin Menu Drawer */}
      <AdminMenuDrawer
        open={menuOpen}
        onOpenChange={setMenuOpen}
        activeSection={activeSection}
        onSelectSection={handleNavigate}
        onExitAdmin={handleExitAdmin}
        user={user}
      />
    </div>
  );
}